<template>
  <Teleport to="body">
    <TransitionGroup name="notify" tag="div" class="notify-container">
      <div
        v-for="item in toasts"
        :key="item.id"
        :class="['notify-toast', `notify-${item.type}`]"
        @mouseenter="pauseToast(item)"
        @mouseleave="resumeToast(item)"
      >
        <div class="notify-icon">
          <svg v-if="item.type === 'success'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          <svg v-else-if="item.type === 'error'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          <svg v-else-if="item.type === 'warning'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
        </div>
        <span class="notify-text">{{ item.message }}</span>
        <button class="notify-close" type="button" @click="removeToast(item.id)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div class="notify-progress" :style="{ animationDuration: item.duration + 'ms' }" :class="{ 'notify-progress-paused': item.paused }"></div>
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<script setup>
import { ref } from 'vue'

const toasts = ref([])
let toastId = 0

const addToast = (message, type = 'info', duration = 3000) => {
  const id = ++toastId
  const toast = { id, message, type, duration, paused: false, timer: null }
  toast.timer = setTimeout(() => removeToast(id), duration)
  toasts.value.push(toast)
  return id
}

const removeToast = (id) => {
  const index = toasts.value.findIndex(t => t.id === id)
  if (index !== -1) {
    clearTimeout(toasts.value[index].timer)
    toasts.value.splice(index, 1)
  }
}

const pauseToast = (toast) => {
  toast.paused = true
  clearTimeout(toast.timer)
}

const resumeToast = (toast) => {
  toast.paused = false
  const remaining = toast.duration * 0.3
  toast.timer = setTimeout(() => removeToast(toast.id), remaining)
}

defineExpose({ addToast })
</script>

<style scoped>
.notify-container {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

.notify-toast {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 280px;
  max-width: 420px;
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04);
  pointer-events: auto;
  overflow: hidden;
  position: relative;
}

.notify-icon {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
}

.notify-success .notify-icon { background: rgba(39, 174, 96, 0.1); color: #27ae60; }
.notify-error .notify-icon { background: rgba(192, 57, 43, 0.1); color: #c0392b; }
.notify-warning .notify-icon { background: rgba(212, 145, 10, 0.1); color: #d4910a; }
.notify-info .notify-icon { background: rgba(52, 152, 219, 0.1); color: #3498db; }

.notify-text {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
  color: #1a1a1a;
  word-break: break-word;
}

.notify-close {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #999;
  cursor: pointer;
  transition: all 0.15s;
}

.notify-close:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #1a1a1a;
}

.notify-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  transform-origin: left;
  animation: notify-shrink linear forwards;
}

.notify-success .notify-progress { background: #27ae60; }
.notify-error .notify-progress { background: #c0392b; }
.notify-warning .notify-progress { background: #d4910a; }
.notify-info .notify-progress { background: #3498db; }

.notify-progress-paused {
  animation-play-state: paused;
}

@keyframes notify-shrink {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

/* Transition */
.notify-enter-active {
  animation: notify-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.notify-leave-active {
  animation: notify-out 0.2s ease-in forwards;
}

@keyframes notify-in {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes notify-out {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.9); }
}
</style>
